import { useRef, useState } from "react"
import styled from "styled-components"
import Button from "@ui/button/Button"
import { systemErrorDetails, systemPost } from "src/services/superAdminApi"
import { formatMinorCurrency } from "src/utils/systemBalance"
import { T } from "../adminTheme"
import { Callout, date, Disclosure, Fact, Facts, FilterBar, Muted, Row, sentence, State, Table } from "./adminUi"

export interface StatementRow {
	section: string; item: string; count: number | null; amount_usd: string; note: string
	trx_count?: number | null; trx_amount_usd?: string; diff_usd?: string
}
export interface IssuerStatement {
	generated_at: string
	company: { uid: string; name: string }
	issuer: number; issuer_label: string
	period: { from: string | null; to: string }
	complete: boolean; errors: string[]; elapsed_ms: number
	buyer_accounts: { candidates: number; with_rows: number; unreadable: Array<{ email: string; error: string }> }
	checks: { cards: number; cards_with_ledger: number; prefunded_cards: number; settlements_without_webhook: number; balance_check: "matches" | "differs" | "needs_full_period"; balance_check_diff_usd: string }
	table: StatementRow[]
	csv: string
}

const MS_PER_SECOND = 1000
const UTC_MINUTES = 16
/** The period is applied in UTC, so it is shown in UTC too, not in the browser's zone. */
const utcMinute = (value: string) => new Date(value).toISOString().slice(0, UTC_MINUTES).replace("T", " ")

const groupBySection = (table: StatementRow[]) => table.reduce<Array<{ section: string; rows: StatementRow[] }>>((groups, row) => {
	const last = groups[groups.length - 1]
	if (last?.section === row.section) last.rows.push(row)
	else groups.push({ section: row.section, rows: [row] })
	return groups
}, [])

const fileName = (report: IssuerStatement, from: string, to: string) =>
	`statement_${report.company.name.replace(/[^\w-]+/g, "_")}_${from || "start"}_${to || "now"}.csv`

const downloadCsv = (csv: string, name: string) => {
	const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
	const link = document.createElement("a")
	link.href = url
	link.download = name
	link.click()
	// Revoking in the same tick can cancel the download in some browsers.
	setTimeout(() => URL.revokeObjectURL(url))
}

const BalanceCheck = ({ checks }: { checks: IssuerStatement["checks"] }) =>
	checks.balance_check === "matches" ? <Callout tone='success' role='status'>Balance matches</Callout>
		: checks.balance_check === "differs" ? <Callout tone='danger'>Difference ${checks.balance_check_diff_usd}</Callout>
			: <Callout tone='info' role='status'>The balance check is only available without a From date</Callout>

/** Amounts come as dollar strings ("-311.00"); shown as "−$311.00" with a real minus sign. */
const usd = (value?: string) => value === undefined || value === "" ? "" : formatMinorCurrency(Math.round(Number(value) * 100), "USD").replace("-", "−")
const isTotal = (row: StatementRow) => row.item.startsWith("TOTAL")
/** The headline number of a section: its TOTAL line, or the only line it has. */
const sectionTotal = (rows: StatementRow[]) => rows.find(isTotal) ?? (rows.length === 1 ? rows[0] : undefined)
const num = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" } as const

const Summary = styled.dl`
	display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; margin: 0;
	> div { background: ${T.surface2}; border: 1px solid ${T.border}; border-radius: ${T.radius}; padding: 12px 14px; }
	dt { color: ${T.muted}; font-size: 12.5px; }
	dd { margin: 4px 0 0; font-size: 17px; font-weight: 650; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
`
const Section = styled.section`
	display: grid; gap: 8px;
	> header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 0 12px; }
	> header b { font-variant-numeric: tabular-nums; }
	table { table-layout: fixed; }
	tr[data-total] td { background: ${T.surface2}; font-weight: 650; border-top: 1px solid ${T.border}; }
`
/** Every section uses the same columns, so Count / Amount / TRX line up down the whole statement. */
const Columns = () => <colgroup><col /><col style={{ width: 90 }} /><col style={{ width: 130 }} /><col style={{ width: 130 }} /><col style={{ width: 120 }} /></colgroup>

const StatementSection = ({ section, rows }: { section: string; rows: StatementRow[] }) => {
	const withTrx = rows.some((row) => row.trx_amount_usd !== undefined)
	const total = sectionTotal(rows)
	return <Section aria-label={sentence(section)}>
		<header><h3>{sentence(section)}</h3>{total && <b>{usd(total.amount_usd)}</b>}</header>
		<Table>
			<Columns />
			<thead><tr><th>Item</th><th style={num}>Count</th><th style={num}>Amount</th><th style={num}>{withTrx && "In TRX"}</th><th style={num}>{withTrx && "Difference"}</th></tr></thead>
			<tbody>{rows.map((row, index) => {
				const off = row.diff_usd !== undefined && row.diff_usd !== "0.00"
				return <tr key={index} data-total={isTotal(row) || undefined}>
					<td>{isTotal(row) ? "Total" : sentence(row.item)}{row.note && <small>{row.note}</small>}</td>
					<td style={num}>{row.count?.toLocaleString("en-US") ?? ""}</td>
					<td style={num}>{usd(row.amount_usd)}</td>
					<td style={num}>{usd(row.trx_amount_usd)}{row.trx_count != null && <small>{row.trx_count.toLocaleString("en-US")} records</small>}</td>
					<td style={{ ...num, color: off ? T.warn : T.success, fontWeight: off ? 650 : 500 }} data-diff={off || undefined}>{row.diff_usd === undefined ? "" : off ? usd(row.diff_usd) : "Matches"}</td>
				</tr>
			})}</tbody>
		</Table>
	</Section>
}

/** Heavy request (issuer ledger of every card): sent only by the button, never on date change. */
export const CompanyStatement = ({ token, companyUid }: { token: string; companyUid: string }) => {
	const [from, setFrom] = useState("")
	const [to, setTo] = useState("")
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState("")
	const [report, setReport] = useState<{ data: IssuerStatement; from: string; to: string } | null>(null)
	const busy = useRef(false)

	const load = async () => {
		if (busy.current) return
		busy.current = true
		setLoading(true)
		setError("")
		// Dates go as YYYY-MM-DD in UTC, exactly as the picker gives them; empty means "from the start" / "now".
		const response = await systemPost<IssuerStatement>(token, "system/company/issuer-statement", { company_uid: companyUid, from: from || undefined, to: to || undefined })
		busy.current = false
		setLoading(false)
		if (response.error) { setError(systemErrorDetails(response.error)); return }
		setReport({ data: response.data, from, to })
	}

	const data = report?.data
	const groups = data ? groupBySection(data.table) : []
	return <div style={{ display: "grid", gap: 16 }}>
		<form onSubmit={(event) => { event.preventDefault(); void load() }}>
			<FilterBar>
				<label>From<input type='date' value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} disabled={loading} /></label>
				<label>To<input type='date' value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} disabled={loading} /></label>
				<Button variant='primarySmall' onClick={() => void load()} disabled={loading}>{loading ? "Building…" : "Build statement"}</Button>
				{data && <Button variant='secondarySmall' onClick={() => downloadCsv(data.csv, fileName(data, report.from, report.to))} disabled={loading}>Download CSV</Button>}
			</FilterBar>
			<Muted style={{ fontSize: 13 }}>Dates are in UTC. Leave From empty to start from the first day of the company; only then the balance check works. Building can take up to a minute.</Muted>
		</form>
		{error && <Callout tone='danger'>{error}</Callout>}
		{loading ? <State loading /> : data && <>
			<Row style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
				<div>
					<h3>{data.company.name} · {data.issuer_label}</h3>
					<Muted style={{ fontSize: 13 }}>{data.period.from ? utcMinute(data.period.from) : "From the start"} — {utcMinute(data.period.to)} UTC · built {date(data.generated_at)} in {(data.elapsed_ms / MS_PER_SECOND).toFixed(1)} s</Muted>
				</div>
				<BalanceCheck checks={data.checks} />
			</Row>
			{!data.complete && <Callout tone='warn' title='The statement is incomplete'>{data.errors.length > 0 && <ul>{data.errors.map((item, index) => <li key={index}>{item}</li>)}</ul>}</Callout>}
			<Summary aria-label='Statement summary'>
				{groups.map((group) => {
					const total = sectionTotal(group.rows)
					return total && <Fact key={group.section} label={sentence(group.section)}>{usd(total.amount_usd)}</Fact>
				})}
			</Summary>
			<div role='group' aria-label='Issuer statement' style={{ display: "grid", gap: 24 }}>
				{groups.map((group) => <StatementSection key={group.section} {...group} />)}
			</div>
			<Disclosure title='Technical details'>
				<Facts>
					<Fact label='Card ledgers read'>{data.checks.cards_with_ledger} of {data.checks.cards}</Fact>
					<Fact label='Prefunded cards'>{data.checks.prefunded_cards}</Fact>
					<Fact label='Settlements without webhook'>{data.checks.settlements_without_webhook}</Fact>
					<Fact label='Buyer accounts'>{data.buyer_accounts.with_rows} with records of {data.buyer_accounts.candidates}</Fact>
				</Facts>
				{data.buyer_accounts.unreadable.length > 0 && <>
					<Muted style={{ fontSize: 13 }}>Buyers without a TRX account (never received funds):</Muted>
					<ul>{data.buyer_accounts.unreadable.map((item, index) => <li key={index}>{item.email} — {item.error}</li>)}</ul>
				</>}
			</Disclosure>
		</>}
	</div>
}
